import React, { FunctionComponent, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { IPostProject, IProject } from '../../../shared/services/project/project.model'
import { Box, Stack } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DialogSaveButtons } from '../miscellaneous-components/dialog-save-buttons';
import { useNavigate } from 'react-router-dom';
import moment, { Moment } from 'moment';
import { IUserDetails } from '../../../shared/services/user-details/user-details.model';
import UserDetailsService from '../../../shared/services/user-details/user-details.service';
import { FormTextInputDisplay } from '../form-field-components/new-and-edit/form-text-input';
import { FormSelectInputDisplay } from '../form-field-components/new-and-edit/form-select-input';
import { FormDateInputDisplay } from '../form-field-components/new-and-edit/form-date-input';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';
import CustomFieldService from '../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../shared/services/custom-field/custom-field.model';

export const ProjectForm: FunctionComponent<{
  newProject: IPostProject | IProject,
  submitHandler: (values: IPostProject) => void,
  isEditPage?: boolean,
  projectActive?: boolean,
  isSubmitting?: boolean | undefined,
  editField?: string,
  setEditField?: React.Dispatch<React.SetStateAction<any>>,
  errorMessage?: string,
  errorCode?: number,
  setErrorMessage?: React.Dispatch<React.SetStateAction<string | undefined>>,
  setErrorCode?: React.Dispatch<React.SetStateAction<number | undefined>>,
}> = ({
  newProject,
  submitHandler,
  isEditPage = false,
  projectActive,
  isSubmitting,
  editField,
  setEditField,
  errorMessage,
  errorCode,
  setErrorMessage,
  setErrorCode,
}) => {
    const navigate = useNavigate()
    //Service
    const userDetailsService = new UserDetailsService();
    const customFieldService = new CustomFieldService()
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [users, setUsers] = useState<IUserDetails[]>([]);
    const [customFields, setCustomFields] = useState<ICustomField[]>();
    //Formik
    const projectSchema = Yup.object().shape({
      summary: Yup.string().required(`You must provide a summary for the ${screenLabelsContext.getLabel('project', { plural: false })}`),
      description: Yup.string().nullable(),
      projectReference: Yup.string().nullable(),
      assignedUserId: Yup.string().nullable(),
      startDate: Yup.string().required(`You must provide a start date for the ${screenLabelsContext.getLabel('project', { plural: false })}`),
      endDate: Yup.string()
        .nullable()
        .test('not-before-start', 'End date cannot be before start date', function (value) {
          if (!value || !this.parent.startDate) return true;
          return moment(value).isSameOrAfter(moment(this.parent.startDate), 'day');
        }),
      ...(customFields && customFields.length > 0 ? customFields.reduce((acc, field) => {
        if (field.isMandatory) {
          acc[field.id] = Yup.string().required(`${field.fieldName} is mandatory`);
        } else {
          acc[field.id] = Yup.string().nullable();
        }
        return acc;
      }, {} as any) : {}),
    })

    const getInitialCustomFieldValues = useCallback(() => {
      if (!customFields || !customFields.length) return {};
      return customFields.reduce((acc, field) => {
        const matchingValue = isEditPage && 'customFieldValues' in newProject && newProject.customFieldValues
          ? newProject.customFieldValues.find(v => v.customFieldId === field.id)?.value ?? ''
          : '';
        acc[field.id] = matchingValue;
        return acc;
      }, {} as Record<string, any>);
    }, [customFields, isEditPage, newProject]);

    const formik = useFormik<any>({
      initialValues: {
        summary: newProject.summary,
        description: newProject.description,
        projectReference: newProject.projectReference ? newProject.projectReference.split(',').join(', ') : null,
        assignedUserId: newProject.assignedUserId,
        startDate: newProject.startDate,
        endDate: newProject.endDate,
        ...getInitialCustomFieldValues(),
      },
      validationSchema: projectSchema,
      onSubmit: (values) => {
        const customFieldValues = Array.isArray(customFields)
          ? customFields.map((field) => ({
            customFieldId: field.id,
            value: values?.[field.id] ?? null,
          }))
          : []
        submitHandler({
          ...values,
          customFieldValues,
        })
      }
    });

    const formikRef = useRef(formik);
    formikRef.current = formik;

    //Use Effects
    useEffect(() => {
      userDetailsService.getAllUserDetails(`?status=active`)
        .then((response) => {
          setUsers(response.filter(user => !(user.inviteSent === true && user.inviteAccepted === false)))
        })
        .catch(err => {
          console.log(err)
        })

      customFieldService.getAllActive(`&entryType=project`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => {
          console.log(err)
        })
    }, [])

    useEffect(() => {
      if (customFields && customFields.length && newProject) {
        const customFieldValues = getInitialCustomFieldValues();
        formikRef.current.setValues({
          summary: newProject.summary,
          description: newProject.description,
          projectReference: newProject.projectReference ? newProject.projectReference.split(',').join(', ') : null,
          assignedUserId: newProject.assignedUserId,
          startDate: newProject.startDate,
          endDate: newProject.endDate,
          ...customFieldValues,
        });
      }
    }, [customFields, newProject, isEditPage, getInitialCustomFieldValues])

    //Formik Event Handlers
    const cancelEditHandler = () => {
      if (setEditField) {
        setEditField('')
      }
      formik.setTouched({})
      formik.setErrors({})
      if (setErrorCode) {
        setErrorCode(undefined)
      }
      if (setErrorMessage) {
        setErrorMessage(undefined)
      }
      if (newProject) {
        const originalCustomFieldValues = getInitialCustomFieldValues();
        formik.setValues({
          summary: newProject.summary,
          description: newProject.description,
          projectReference: newProject.projectReference ? newProject.projectReference.split(',').join(', ') : null,
          assignedUserId: newProject.assignedUserId,
          startDate: newProject.startDate,
          endDate: newProject.endDate,
          ...originalCustomFieldValues,
        })
      }
    }

    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        const updatedValues = { ...formik.values, [name]: date.format('YYYY-MM-DD') }
        formik.setValues(updatedValues, false).then(() => {
          if (name === 'startDate' && updatedValues.endDate) {
            formik.validateField('endDate')
          }
        })
      } else if (name === 'endDate') {
        formik.setValues({ ...formik.values, [name]: null })
      }
    }

    return (
      <Box component='form' onSubmit={formik.handleSubmit}>
        <FormTextInputDisplay
          isTopRow={true}
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'summary'}
          title='Summary'
          name='summary'
          value={formik.values.summary}
          touched={formik.touched.summary}
          errors={formik.errors.summary}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!projectActive}
          customErrorMessage={errorCode === 1 ? errorMessage : undefined}
        />
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'projectReference'}
          title={`${screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })} Reference`}
          name='projectReference'
          value={formik.values.projectReference || ''}
          touched={formik.touched.projectReference}
          errors={formik.errors.projectReference}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!projectActive}
          tooltip='Enter multiple references separated by commas (e.g., REF1, REF2, REF3)'
        />
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'description'}
          title='Description'
          name='description'
          value={formik.values.description}
          touched={formik.touched.description}
          errors={formik.errors.description}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!projectActive}
        />
        <FormDateInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'startDate'}
          title='Start Date'
          name='startDate'
          value={moment(formik.values.startDate)}
          touched={formik.touched.startDate}
          errors={formik.errors.startDate}
          onChange={(date: any) => dateHandler(date, 'startDate')}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!projectActive}
          customErrorMessage={errorCode === 2 ? errorMessage : undefined}
        />
        <FormDateInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'endDate'}
          title='End Date'
          name='endDate'
          value={formik.values.endDate ? moment(formik.values.endDate) : null}
          touched={formik.touched.endDate}
          errors={formik.errors.endDate}
          onChange={(date: any) => dateHandler(date, 'endDate')}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!projectActive}
          minDate={formik.values.startDate ? moment(formik.values.startDate) : undefined}
          customErrorMessage={errorCode === 3 ? errorMessage : undefined}
        />
        <FormSelectInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'assignedUserId'}
          title='Default Assignee'
          name='assignedUserId'
          value={formik.values.assignedUserId}
          touched={formik.touched.assignedUserId}
          errors={formik.errors.assignedUserId}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!projectActive}
          array={users.sort((a, b) => a.firstName.localeCompare(b.firstName))}
          valueKey={'id'}
          labelKey={(user) => `${user.firstName} ${user.surname}`}
          isBottomRow={customFields && customFields.length > 0 ? false : true}
        />
        {customFields && customFields.length > 0 && customFields.map((field, index) => (
          <FormTextInputDisplay
            key={field.id}
            isBottomRow={index === customFields.length - 1}
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== field.id}
            title={field.fieldName}
            name={field.id}
            type={field.fieldType === 'number' ? 'number' as const : 'text' as const}
            value={formik.values[field.id]}
            touched={formik.touched[field.id]}
            errors={formik.errors[field.id]}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={!projectActive}
          />
        ))
        }
        {!isEditPage &&
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isSubmitting}
              cancelHandler={() => {
                formik.setTouched({})
                formik.setErrors({})
                if (setErrorMessage) {
                  setErrorMessage(undefined)
                }
                if (setErrorCode) {
                  setErrorCode(undefined)
                }
                navigate('/organisation/projects')
              }}
              submitHandler={formik.submitForm}
            />
          </Stack>
        }
      </Box >
    )
  }